<template>
  <div id="app" :class="{ 'forum-bg': $route.path.startsWith('/forum') }">
    <!-- 只在非论坛和非管理端显示主站导航栏 -->
    <nav v-if="!isAdminRoute && !isForumRoute" class="sci-fi-nav">
      <div class="nav-container">
        <div class="nav-brand">
          <img src="@/assets/logo.png" alt="Logo" class="nav-logo-img" />
          <span class="nav-logo">大气校正</span>
        </div>
        
        <div class="nav-links">
          <router-link 
            v-for="(item, index) in navLinks" 
            :key="index"
            :to="item.path" 
            class="nav-link">
            {{ item.title }}
            <div class="link-underline"></div>
          </router-link>
          <!-- 论坛链接，新标签页打开 -->
          <!-- <router-link to="/forum" class="nav-link">
            论坛
            <div class="link-underline"></div>
          </router-link> -->
        </div>

        <!-- 访问量显示 -->
        <div class="visit-stats">
          <div class="visit-item">
            <span class="visit-label">总访问</span>
            <span class="visit-number">{{ visitStats.totalVisits || 0 }}</span>
          </div>
          <div class="visit-item">
            <span class="visit-label">今日</span>
            <span class="visit-number">{{ visitStats.todayVisits || 0 }}</span>
          </div>
        </div>

        <div class="auth-links">
          <!-- 未登录状态显示登录和注册按钮 -->
          <template v-if="!isAuthenticated">
            <router-link to="/login" class="nav-link">登录</router-link>
            <router-link to="/register" class="nav-link">注册</router-link>
          </template>
          <!-- 已登录状态显示用户名和退出按钮 -->
          <template v-else>
            <div class="user-menu">
              <span class="user-name">{{ currentUser.name }}</span>
              <div class="dropdown-menu">
                <router-link to="/user" class="dropdown-item">个人中心</router-link>
                <a href="#" @click.prevent="handleLogout" class="dropdown-item">退出登录</a>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div class="nav-glow"></div>
    </nav>

    <!-- 只在非论坛和非管理端显示大气粒子动画 -->
    <div v-if="!isAdminRoute && !isForumRoute" class="atmosphere-container">
      <!-- 大气粒子 -->
      <div v-for="n in 30" :key="`particle-${n}`" class="atmosphere-particle" :style="{
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDelay: Math.random() * 10 + 's',
        animationDuration: (8 + Math.random() * 12) + 's'
      }"></div>
      
      <!-- 数据流粒子 -->
      <div v-for="n in 15" :key="`data-${n}`" class="data-particle" :style="{
        left: Math.random() * 100 + '%',
        top: Math.random() * 100 + '%',
        animationDelay: Math.random() * 5 + 's',
        animationDuration: (6 + Math.random() * 8) + 's'
      }"></div>
      
      <!-- 科技网格 -->
      <div class="tech-grid"></div>
      
      <!-- 光晕效果 -->
      <div class="atmosphere-glow"></div>
    </div>

    <!-- 主内容区 -->
    <main>
      <router-view/>
    </main>

    <!-- 只在非论坛和非管理端显示主站页脚 -->
    <footer v-if="!isAdminRoute && !isForumRoute" class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>版权信息</h4>
          <p>&copy; {{ new Date().getFullYear() }} 大气校正软件开发团队</p>
          <p>保留所有权利</p>
        </div>
        
        <div class="footer-section">
          <h4>联系我们</h4>
          <p>邮箱：gaohl@radi.ac.cn</p>
          <p>电话： 13810191124</p>
          <p>地址：北京市海淀区邓庄南路9号 中国科学院空天信息创新研究院</p>
        </div>
        
        <div class="footer-section social-links">
          <h4>关注我们</h4>
          <div class="social-icons">
            <a href="" target="_blank">
              <!-- https://github.com/ -->
              <GithubIcon class="icon" />
            </a>
            <a href="" target="_blank">
              <!-- https://weixin.qq.com/ -->
              <WechatIcon class="icon" />
            </a>
            <a href="" target="_blank">
              <!-- https://www.alipay.com/atmos -->
              <AlipayIcon class="icon" />
            </a>
          </div>
        </div>
      </div>
      
      <div class="footer-links">
        <a href="https://data.cresda.cn/#/2dMap" target="_blank">陆地卫星观测服务中心</a> | 
        <a href="https://www.gscloud.cn/" target="_blank">地理空间数据云</a> | 
        <a href="https://earthexplorer.usgs.gov/" target="_blank">哨兵数据</a>
      </div>
      
      <div class="footer-bottom">
        <p>网站技术支持：大气校正软件开发团队 | 冀ICP备2025121071号</p>
      </div>
    </footer>
  </div>
</template>

<script>
import GithubIcon from '@/components/Icons/GithubIcon.vue'
import WechatIcon from '@/components/Icons/WechatIcon.vue'
import AlipayIcon from '@/components/Icons/AlipayIcon.vue'
import { mapGetters, mapActions } from 'vuex'
import request from '@/utils/request'
import { onMounted } from 'vue'
import { computed } from 'vue'
import { useRoute } from 'vue-router'

export default {
  name: 'App',
  components: {
    GithubIcon,
    WechatIcon,
    AlipayIcon
  },
  data() {
    return {
      atmosphereInterval: null,
      visitStats: {
        totalVisits: 0,
        todayVisits: 0,
        uniqueIPs: 0,
        weeklyTrend: []
      },
      navLinks: [
        { title: '首页', path: '/' },
        { title: '产品介绍', path: '/introduction' },
        // { title: '产品案例', path: '/case' },
        { title: '产品教程', path: '/tutorials' },
        { title: '产品评价', path: '/reviews' },
        { title: '下载地址', path: '/download' },
        
        // 从这里移除论坛链接
      ]
    }
  },
  computed: {
    ...mapGetters('user', ['isAuthenticated', 'currentUser']),
    isAdminRoute() {
      return this.$route.path.startsWith('/admin')
    },
    isForumRoute() {
      return this.$route && this.$route.path && this.$route.path.startsWith('/forum')
    }
  },
  methods: {
    ...mapActions('user', ['logout', 'checkAuth']),
    
    // 获取浏览器信息
    getBrowserInfo() {
      const userAgent = navigator.userAgent
      let browser = 'Unknown'
      let os = 'Unknown'
      
      // 检测浏览器
      if (userAgent.includes('Chrome')) {
        browser = 'Chrome'
      } else if (userAgent.includes('Firefox')) {
        browser = 'Firefox'
      } else if (userAgent.includes('Safari')) {
        browser = 'Safari'
      } else if (userAgent.includes('Edge')) {
        browser = 'Edge'
      } else if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) {
        browser = 'Internet Explorer'
      }
      
      // 检测操作系统
      if (userAgent.includes('Windows')) {
        os = 'Windows'
      } else if (userAgent.includes('Mac')) {
        os = 'macOS'
      } else if (userAgent.includes('Linux')) {
        os = 'Linux'
      } else if (userAgent.includes('Android')) {
        os = 'Android'
      } else if (userAgent.includes('iOS')) {
        os = 'iOS'
      }
      
      return { browser, os }
    },
    
    // 记录访问信息
    async recordVisit() {
      try {
        // 只在非管理端页面记录访问
        if (this.isAdminRoute) {
          return
        }
        
        const { browser, os } = this.getBrowserInfo()
        const pageUrl = window.location.href
        const referrer = document.referrer || ''
        const userAgent = navigator.userAgent
        
        // 获取用户ID（如果已登录）
        const userToken = localStorage.getItem('userToken')
        let userId = null
        if (userToken && this.isAuthenticated && this.currentUser) {
          userId = this.currentUser.id
        }
        
        // 获取IP地址（使用多个备选方案）
        let ipAddress = null
        let geoInfo = null
        
        try {
          // 方案1：使用ipapi.co获取IP和地理位置信息
          const geoResponse = await fetch('https://ipapi.co/json/', {
            method: 'GET',
            mode: 'cors',
            headers: {
              'Accept': 'application/json'
            }
          })
          if (geoResponse.ok) {
            const geoData = await geoResponse.json()
            ipAddress = geoData.ip
            geoInfo = {
              country: geoData.country_name,
              region: geoData.region,
              city: geoData.city
            }
          }
        } catch (geoError) {
          console.warn('ipapi.co获取地理位置失败:', geoError)
        }
        
        // 方案2：如果方案1失败，尝试使用ip-api.com
        if (!ipAddress) {
          try {
            const ipResponse2 = await fetch('http://ip-api.com/json/', {
              method: 'GET',
              mode: 'cors'
            })
            if (ipResponse2.ok) {
              const ipData2 = await ipResponse2.json()
              ipAddress = ipData2.query
              geoInfo = {
                country: ipData2.country,
                region: ipData2.regionName,
                city: ipData2.city
              }
            }
          } catch (ipError2) {
            console.warn('ip-api.com获取IP失败:', ipError2)
          }
        }
        
        // 方案3：尝试使用国内的地理位置服务
        if (!ipAddress || !geoInfo) {
          try {
            const cnGeoResponse = await fetch('https://ip.useragentinfo.com/json', {
              method: 'GET',
              mode: 'cors'
            })
            if (cnGeoResponse.ok) {
              const cnGeoData = await cnGeoResponse.json()
              if (!ipAddress) ipAddress = cnGeoData.ip
              if (!geoInfo) {
                geoInfo = {
                  country: cnGeoData.country,
                  region: cnGeoData.province,
                  city: cnGeoData.city
                }
              }
            }
          } catch (cnGeoError) {
            console.warn('国内地理位置服务获取失败:', cnGeoError)
          }
        }
        
        // 方案4：如果前几个方案都失败，尝试使用httpbin.org只获取IP
        if (!ipAddress) {
          try {
            const ipResponse3 = await fetch('https://httpbin.org/ip', {
              method: 'GET',
              mode: 'cors'
            })
            if (ipResponse3.ok) {
              const ipData3 = await ipResponse3.json()
              ipAddress = ipData3.origin
            }
          } catch (ipError3) {
            console.warn('httpbin.org获取IP失败:', ipError3)
          }
        }
        
        // 如果所有方案都失败，使用本地IP
        if (!ipAddress) {
          ipAddress = '127.0.0.1'
        }
        
        // 发送访问记录到后端，包含地理位置信息
        await request.post('/admin/dashboard/record', {
          ip_address: ipAddress,
          page_url: pageUrl,
          browser: browser,
          os: os,
          referrer: referrer,
          user_agent: userAgent,
          user_id: userId,
          admin_id: null,
          geo_info: geoInfo // 添加地理位置信息
        })
      } catch (error) {
        console.error('记录访问信息失败:', error)
        // 静默处理错误，不影响用户体验
      }
    },
    
    // 获取访问量统计
    async getVisitStats() {
      try {
        const response = await request.get('/auth/visit-stats')
        // 由于request拦截器已经返回了response.data，所以这里直接使用response
        this.visitStats = {
          totalVisits: response.totalVisits || 0,
          todayVisits: response.todayVisits || 0,
          uniqueIPs: response.uniqueIPs || 0,
          weeklyTrend: response.weeklyTrend || []
        }
      } catch (error) {
        console.error('获取访问量统计失败:', error)
        // 设置默认值，不影响用户体验
        this.visitStats = {
          totalVisits: 0,
          todayVisits: 0,
          uniqueIPs: 0,
          weeklyTrend: []
        }
      }
    },
    
    async handleLogout() {
      await this.logout();
      this.$router.push('/login');
    }
  },
  mounted() {
    // 检查登录状态
    this.checkAuth();
    
    // 记录初始访问
    this.recordVisit();
    
    // 获取访问量统计
    this.getVisitStats();

    // 门户跳转论坛并自动传递 token
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    if (token) {
      localStorage.setItem('token', token)
      // 去掉 token 参数
      const url = new URL(window.location)
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url)
    }
    console.log('当前路由：', this.$route.path)
    setTimeout(() => {
      console.log('1秒后路由：', this.$route.path)
    }, 1000)
  },
  watch: {
    // 监听路由变化，记录页面访问
    '$route'(to, from) {
      // 延迟记录，确保页面已加载
      this.$nextTick(() => {
        this.recordVisit();
      });
    }
  }
}
</script>
<style>
:root {
  --primary: #00B4FF;
  --secondary: #00F7FF;
  --accent: #FF00FF;
  --bg-dark: #0A0A0F;
  --bg-darker: #050507;
  --text-primary: #E0E0FF;
  --text-secondary: #B0B0FF;
  --nav-bg: rgba(10, 10, 15, 0.95);
  --nav-glow: rgba(0, 180, 255, 0.2);
  --text-color: rgba(224, 224, 255, 0.9);
  --carbon-fiber: linear-gradient(45deg, #1A1A1A 25%, #2A2A2A 25%, #2A2A2A 50%, #1A1A1A 50%, #1A1A1A 75%, #2A2A2A 75%);
  --quantum-glow: linear-gradient(135deg, var(--primary), var(--secondary));
}

body {
  background-color: var(--bg-dark);
  color: var(--text-primary);
  background-image: 
    radial-gradient(circle at 50% 50%, rgba(0, 180, 255, 0.1) 0%, transparent 50%),
    linear-gradient(45deg, rgba(0, 247, 255, 0.05) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(255, 0, 255, 0.05) 25%, transparent 25%);
  background-size: 100% 100%, 60px 60px, 60px 60px;
  background-position: center center, 0 0, 0 0;
  min-height: 100vh;
  margin: 0;
  font-family: 'Orbitron', sans-serif;
}

/* 论坛独立背景样式 */
.forum-bg {
  background: #f6f8fa !important;
  color: #222 !important;
  background-image: none !important;
}

.sci-fi-nav {
  background: var(--nav-bg);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 180, 255, 0.2);
  position: relative;
  z-index: 1000;
  box-shadow: 0 0 20px rgba(0, 180, 255, 0.1);
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-logo-img {
  height: 48px;
  width: auto;
}

.nav-brand .nav-logo {
  font-family: "STZhongsong", "SimSun", serif;
  font-size: 28px;
  font-weight: 600;
  color: #1f2937;
  text-decoration: none;
  position: relative;
}

.nav-brand .nav-logo::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, #409EFF, #67C23A);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.nav-brand .nav-logo:hover::after {
  transform: scaleX(1);
}

.nav-links {
  display: flex;
  gap: 2rem;
  flex-grow: 1;
  justify-content: center;
}

/* 访问量显示样式 */
.visit-stats {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin-right: 1rem;
}

.visit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  background: rgba(0, 180, 255, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(0, 180, 255, 0.2);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.visit-item:hover {
  background: rgba(0, 180, 255, 0.15);
  border-color: rgba(0, 180, 255, 0.4);
  box-shadow: 0 0 15px rgba(0, 180, 255, 0.3);
}

.visit-label {
  font-size: 0.7rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.visit-number {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--primary);
  text-shadow: 0 0 8px rgba(0, 180, 255, 0.5);
}

.auth-links {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.nav-link {
  color: var(--text-color);
  text-decoration: none;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.9rem;
}

.nav-link:hover {
  color: var(--primary);
  text-shadow: 0 0 8px var(--primary);
}

.link-underline {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: var(--quantum-glow);
  transition: all 0.3s ease;
  filter: blur(1px);
}

.nav-link:hover .link-underline {
  width: 80%;
  box-shadow: 0 0 10px var(--primary);
}

.nav-glow {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, 
    transparent, 
    var(--primary),
    var(--secondary),
    transparent);
  box-shadow: 0 0 20px var(--nav-glow);
  animation: glowFlow 3s infinite;
  filter: blur(1px);
}

@keyframes glowFlow {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.7; }
}

.atmosphere-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 999;
  overflow: hidden;
}

.atmosphere-particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: radial-gradient(circle, var(--primary) 0%, transparent 70%);
  border-radius: 50%;
  box-shadow: 0 0 12px var(--primary);
  animation: atmosphereFloat linear infinite;
  opacity: 0.6;
}

@keyframes atmosphereFloat {
  0% {
    transform: translateY(100vh) translateX(0) scale(0.5);
    opacity: 0;
  }
  10% {
    opacity: 0.8;
    transform: translateY(90vh) translateX(20px) scale(1);
  }
  50% {
    opacity: 0.6;
    transform: translateY(50vh) translateX(-30px) scale(0.8);
  }
  90% {
    opacity: 0.8;
    transform: translateY(10vh) translateX(40px) scale(1.2);
  }
  100% {
    transform: translateY(-10vh) translateX(0) scale(0.5);
    opacity: 0;
  }
}

.data-particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: radial-gradient(circle, var(--secondary) 0%, transparent 70%);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--secondary);
  animation: dataStream linear infinite;
  opacity: 0.7;
}

@keyframes dataStream {
  0% {
    transform: translateY(100vh) translateX(-50px) scale(0.3);
    opacity: 0;
  }
  20% {
    opacity: 0.9;
    transform: translateY(80vh) translateX(30px) scale(1);
  }
  60% {
    opacity: 0.7;
    transform: translateY(40vh) translateX(-20px) scale(0.7);
  }
  80% {
    opacity: 0.9;
    transform: translateY(20vh) translateX(50px) scale(1.1);
  }
  100% {
    transform: translateY(-10vh) translateX(0) scale(0.3);
    opacity: 0;
  }
}

.tech-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 180, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 180, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
  pointer-events: none;
}

@keyframes gridMove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(50px, 50px);
  }
}

.atmosphere-glow {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100px;
  background: linear-gradient(to top, 
    rgba(0, 180, 255, 0.1) 0%,
    rgba(0, 247, 255, 0.05) 50%,
    transparent 100%);
  animation: glowPulse 4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes glowPulse {
  0%, 100% {
    opacity: 0.3;
    transform: scaleY(1);
  }
  50% {
    opacity: 0.6;
    transform: scaleY(1.2);
  }
}

/* 用户菜单样式 */
.user-menu {
  position: relative;
  cursor: pointer;
}

.user-name {
  color: var(--primary);
  font-weight: 500;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  min-width: 150px;
  display: none;
  z-index: 1000;
}

.user-menu:hover .dropdown-menu {
  display: block;
}

.dropdown-item {
  display: block;
  padding: 0.5rem 1rem;
  color: var(--text-color);
  text-decoration: none;
  transition: background 0.3s;
}

.dropdown-item:hover {
  background: rgba(0, 82, 204, 0.1);
  color: var(--primary);
}

@media (max-width: 768px) {
  .nav-container {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
  
  .nav-links {
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .visit-stats {
    order: -1;
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
  
  .auth-links {
    justify-content: center;
  }
}

/* 全局页脚样式 */
.footer {
  background: var(--bg-darker);
  border-top: 1px solid rgba(0, 180, 255, 0.2);
  color: var(--text-primary);
  position: relative;
  overflow: hidden;
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--quantum-glow);
  filter: blur(1px);
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  padding-bottom: 20px;
}

.footer-section {
  flex: 1;
  min-width: 250px;
}

.footer-section h4 {
  color: var(--primary);
  text-shadow: 0 0 10px rgba(0, 180, 255, 0.5);
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.footer-section p {
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 8px 0;
  font-size: 0.9rem;
}

.social-icons {
  display: flex;
  gap: 15px;
  margin-top: 10px;
}

.social-icons a {
  color: var(--primary);
  transition: color 0.3s;
}

.social-icons a:hover {
  color: var(--secondary);
}

.icon {
  width: 24px;
  height: 24px;
}

.footer-links {
  border-top: 1px solid rgba(0, 180, 255, 0.1);
  padding-top: 20px;
  margin-top: 20px;
  text-align: center;
}

.footer-links a {
  color: var(--secondary);
  text-decoration: none;
  margin: 0 10px;
  transition: all 0.3s ease;
}

.footer-links a:hover {
  color: var(--primary);
  text-shadow: 0 0 8px var(--primary);
}

.footer-bottom {
  color: var(--text-secondary);
  border-top: 1px solid rgba(0, 180, 255, 0.1);
  margin-top: 20px;
  text-align: center;
  font-size: 0.8rem;
}

/* 添加故障艺术效果 */
@keyframes glitch {
  0% {
    transform: translate(0);
  }
  20% {
    transform: translate(-2px, 2px);
  }
  40% {
    transform: translate(-2px, -2px);
  }
  60% {
    transform: translate(2px, 2px);
  }
  80% {
    transform: translate(2px, -2px);
  }
  100% {
    transform: translate(0);
  }
}

.glitch-effect {
  position: relative;
}

.glitch-effect::before,
.glitch-effect::after {
  content: attr(data-text);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.8;
}

.glitch-effect::before {
  color: var(--accent);
  animation: glitch 0.3s infinite;
  clip-path: polygon(0 0, 100% 0, 100% 45%, 0 45%);
  transform: translate(-2px, -2px);
}

.glitch-effect::after {
  color: var(--primary);
  animation: glitch 0.3s infinite reverse;
  clip-path: polygon(0 55%, 100% 55%, 100% 100%, 0 100%);
  transform: translate(2px, 2px);
}
</style>


